/*****************************************************************
 *  CUSTOMER BUTLER - THE PAGES' CALLS TO THE SERVER               *
 *  File: apps/web/src/pages/customer/customerApi.js               *
 *  What it does: every call the clean customer pages make, in one *
 *  place, each named for what it asks. Customer Butler's own      *
 *  doors live at /api/customer/...; the past-reports list comes   *
 *  from the Report Engine's read-only lookup door (THE WALL: the  *
 *  two modules talk only through that HTTP door).                 *
 *  Reads: the doors.  Writes: through the doors only. Every write *
 *  says which page sent it (the X-Butler-Page header), so the     *
 *  audit row's default why can name the screen (BACKLOG 312).     *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 0 - SENDING A CHANGE (says which page sent it)         *
 *  SECTION 1 - LAYOUT AND SETUP                                   *
 *  SECTION 2 - CUSTOMERS                                          *
 *  SECTION 3 - PROPERTIES                                         *
 *  SECTION 4 - OFFICE NOTES                                       *
 *  SECTION 5 - ACTIVITIES, CONTACTS, CONSENT                      *
 *  SECTION 5b - FROM THE OTHER BUTLERS (timeline entries)         *
 *  SECTION 6 - PAST REPORTS (the Report Engine's door)            *
 *  SECTION 7 - THE HR LOOK'S COUNTS (list totals, Zoho Bridge)    *
 *  SECTION 8 - A CUSTOMER'S TASKS (open / closed activities)      *
 *****************************************************************/

import { apiGet, authFetch, API } from '../../lib/api';

/* ---- clockOffsetMinutes() -- this device's offset from UTC ----
   Sent with list requests so "New This Week" means the person's
   own week (see the server's services/listViews.js).            */
function clockOffsetMinutes() {
  const offsetNow = new Date().getTimezoneOffset();

  return offsetNow;
}

/* ---- buildQuery(queryParts) -- { a: 1, b: '' } -> "a=1" ---- */
function buildQuery(queryParts) {
  const searchParams = new URLSearchParams();

  for (const [partName, partValue] of Object.entries(queryParts)) {
    const hasValue = partValue !== undefined && partValue !== null && partValue !== '';

    if (hasValue) {
      searchParams.set(partName, String(partValue));
    }
  }

  return searchParams.toString();
}

/* ---- readPage(path) --------------------------------------------
   A list door's whole answer: { rows, meta }. apiGet keeps only
   the data, and a list needs the paging meta too.               */
async function readPage(path) {
  const pageResponse = await authFetch(`${API}${path}`);

  if (!pageResponse.ok) {
    throw new Error(`The list could not be loaded (${pageResponse.status})`);
  }

  const pageJson = await pageResponse.json();
  const rows = Array.isArray(pageJson.data) ? pageJson.data : [];
  const meta = pageJson.meta || {};

  return { rows, meta };
}

/*****************************************************************
 *  SECTION 0 - SENDING A CHANGE                                   *
 *  WHY (2026-10-14, James; BACKLOG 312): every change is recorded *
 *  with who, when and why. When nobody types a why, the server    *
 *  writes one that names the screen ("added from the customer     *
 *  record page"), so each change here says which page sent it.    *
 *****************************************************************/

// The header the server reads to name the screen (services/recordChange.js).
const PAGE_HEADER_NAME = 'X-Butler-Page';

/* ---- readThisPageAddress() -- e.g. '/app/customers/123' ----
   Only the path: never the query, never anything typed.        */
function readThisPageAddress() {
  const hasWindow = typeof window !== 'undefined' && window.location;

  if (!hasWindow) {
    return '';
  }

  return window.location.pathname || '';
}

/* ---- sendChange(path, method, body) ----------------------------
   Sends one change to a Customer Butler door, saying which page
   sent it. Out: { ok, data, error, status }, the same shape the
   suite's apiSend gives, so the pages read it the same way.     */
async function sendChange(path, method, body) {
  const doorResponse = await authFetch(`${API}${path}`, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    headers: { [PAGE_HEADER_NAME]: readThisPageAddress() },
  });

  let doorJson = null;

  try {
    doorJson = await doorResponse.json();
  } catch (noBody) {
    doorJson = null;
  }

  const hasData = Boolean(doorJson) && doorJson.data !== undefined;
  const hasError = Boolean(doorJson) && Boolean(doorJson.error);

  return {
    ok: doorResponse.ok,
    data: hasData ? doorJson.data : doorJson,
    error: hasError ? doorJson.error : null,
    status: doorResponse.status,
  };
}

/*****************************************************************
 *  SECTION 1 - LAYOUT AND SETUP                                   *
 *****************************************************************/

/* ---- fetchLayout(entityType) -- the screen layout door ---- */
export function fetchLayout(entityType) {
  return apiGet(`/customer/layouts/${entityType}`);
}

/* ---- fetchBuiltinChoices(entityType) -- CRM Setup's list ---- */
export function fetchBuiltinChoices(entityType) {
  return apiGet(`/customer/layouts/${entityType}/builtins`);
}

/* ---- saveBuiltinChoice(entityType, fieldKey, isShown, why) ---- */
export function saveBuiltinChoice(entityType, fieldKey, isShown, why) {
  return sendChange(`/customer/layouts/${entityType}/builtins/${fieldKey}`, 'PUT', { isShown, why });
}

/*****************************************************************
 *  SECTION 2 - CUSTOMERS                                          *
 *****************************************************************/

/* ---- fieldFiltersText(filters) -- the ?filters JSON, or '' when none ---- */
function fieldFiltersText(filters) {
  const usable = (filters || []).filter((oneFilter) => oneFilter && oneFilter.key && oneFilter.contains);

  if (usable.length === 0) {
    return '';
  }

  return JSON.stringify(usable.map((oneFilter) => ({ key: oneFilter.key, contains: oneFilter.contains })));
}

/* ---- fetchCustomerPage({ search, view, sort, page, limit, filters, withNextTask }) ----
   Out: { rows, meta: { page, totalPages, total, view } }.
   filters (2026-10-15): Zoho's "Filter By Fields" - [{ key, contains }].
   withNextTask: true adds each row's soonest open task (card rows). */
export function fetchCustomerPage(listChoice) {
  const queryText = buildQuery({
    search: listChoice.search,
    view: listChoice.view,
    sort: listChoice.sort,
    page: listChoice.page,
    limit: listChoice.limit,
    filters: fieldFiltersText(listChoice.filters),
    withNextTask: listChoice.withNextTask ? '1' : '',
    tzOffset: clockOffsetMinutes(),
  });

  return readPage(`/customer/customers?${queryText}`);
}

/* ---- fetchCustomer(customerId) -- one customer with properties + contacts ---- */
export function fetchCustomer(customerId) {
  return apiGet(`/customer/customers/${customerId}`);
}

/* ---- saveCustomer(customerId, saveBody) -- { ok, data, error } ---- */
export function saveCustomer(customerId, saveBody) {
  return sendChange(`/customer/customers/${customerId}`, 'PUT', saveBody);
}

/*****************************************************************
 *  SECTION 3 - PROPERTIES                                         *
 *****************************************************************/

/* ---- fetchPropertyPage({ search, view, sort, page, limit }) ---- */
export function fetchPropertyPage(listChoice) {
  const queryText = buildQuery({
    search: listChoice.search,
    view: listChoice.view,
    sort: listChoice.sort,
    page: listChoice.page || 1,
    limit: listChoice.limit,
    tzOffset: clockOffsetMinutes(),
  });

  return readPage(`/customer/properties?${queryText}`);
}

/* ---- fetchProperty(propertyId) -- property + owner + linked people ---- */
export function fetchProperty(propertyId) {
  return apiGet(`/customer/properties/${propertyId}`);
}

/* ---- saveProperty(propertyId, saveBody) -- { ok, data, error } ---- */
export function saveProperty(propertyId, saveBody) {
  return sendChange(`/customer/properties/${propertyId}`, 'PUT', saveBody);
}

/* ---- addProperty(propertyBody) -- { ok, data, error } ----
   propertyBody may carry `why`, typed on the Add property sheet. */
export function addProperty(propertyBody) {
  return sendChange('/customer/properties', 'POST', propertyBody);
}

/* ---- fetchPropertyHistory(propertyId) -- the property's activity ---- */
export function fetchPropertyHistory(propertyId) {
  return apiGet(`/customer/properties/${propertyId}/history`);
}

/*****************************************************************
 *  SECTION 4 - OFFICE NOTES                                       *
 *****************************************************************/

/* ---- fetchOfficeNotes(customerId) -- every note, removed ones too ---- */
export function fetchOfficeNotes(customerId) {
  return apiGet(`/customer/customers/${customerId}/office-notes?includeRemoved=true`);
}

/* ---- addOfficeNote(customerId, noteText) ---- */
export function addOfficeNote(customerId, noteText) {
  return sendChange(`/customer/customers/${customerId}/office-notes`, 'POST', { text: noteText });
}

/* ---- takeOfficeNoteOff(customerId, noteId, why) ----------------
   Takes a note off the board; it is kept, with who / when / why. */
export function takeOfficeNoteOff(customerId, noteId, why) {
  return sendChange(`/customer/customers/${customerId}/office-notes/${noteId}`, 'DELETE', { why });
}

/*****************************************************************
 *  SECTION 5 - ACTIVITIES, CONTACTS, CONSENT                      *
 *****************************************************************/

/* ---- fetchTimeline(customerId) -- every logged touch ---- */
export function fetchTimeline(customerId) {
  return apiGet(`/customer/customers/${customerId}/timeline`);
}

/* ---- logActivity(customerId, activityBody) -- { channel, direction, subject, body } ---- */
export function logActivity(customerId, activityBody) {
  return sendChange(`/customer/customers/${customerId}/communications`, 'POST', activityBody);
}

/* ---- addContact(contactBody) -- { customerId, firstName, ... } ----
   contactBody may carry `why`, typed on the Add contact sheet.  */
export function addContact(contactBody) {
  return sendChange('/customer/contacts', 'POST', contactBody);
}

/* ---- fetchConsent(customerId) ---- */
export function fetchConsent(customerId) {
  return apiGet(`/customer/customers/${customerId}/consent`);
}

/* ---- recordConsent(customerId, consentBody) ---- */
export function recordConsent(customerId, consentBody) {
  return sendChange(`/customer/customers/${customerId}/consent`, 'POST', consentBody);
}

/*****************************************************************
 *  SECTION 5b - FROM THE OTHER BUTLERS (2026-10-14)               *
 *  What the Report Engine, Dispatch, On-Call, the water module    *
 *  and Schedule told Customer Butler they did. Read-only, except  *
 *  an admin attaching a waiting entry to a customer.              *
 *****************************************************************/

/* ---- fetchSuiteTimeline(customerId) -- newest first ---- */
export function fetchSuiteTimeline(customerId) {
  return apiGet(`/customer/timeline/customers/${customerId}`);
}

/* ---- fetchPropertySuiteTimeline(propertyId) -- newest first ---- */
export function fetchPropertySuiteTimeline(propertyId) {
  return apiGet(`/customer/timeline/properties/${propertyId}`);
}

/* ---- fetchHoldingEntries() -- entries no customer was found for ---- */
export function fetchHoldingEntries() {
  return apiGet('/customer/timeline/holding');
}

/* ---- fetchDoorRefusals() -----------------------------------------
   Every call the timeline door refused (CRM Setup, BACKLOG 317,
   2026-10-15). Out: { refusals, showsUnprovenCalls,
   countedNotWrittenYet }. Reading only.                         */
export function fetchDoorRefusals() {
  return apiGet('/customer/timeline/refused');
}

/* ---- attachHoldingEntry(entryId, customerId, why) --------------
   Adds the entry to that customer's timeline (a new row; the
   waiting one is kept). Out: { ok, data, error }.               */
export function attachHoldingEntry(entryId, customerId, why) {
  return sendChange(`/customer/timeline/holding/${entryId}/attach`, 'POST', { customerId, why });
}

/*****************************************************************
 *  SECTION 6 - PAST REPORTS (the Report Engine's lookup door)     *
 *  Contract (Report Engine builder, 2026-10-14):                  *
 *    GET /report-engine/api/reports/lookup?email=&address=        *
 *    signed in by the suite's own login cookie;                   *
 *    { reports: [{ id, address, type, inspectionDate, sentAt,     *
 *      status, url }] } newest first; both params = match either; *
 *    401 not signed in / not WNCIL, 400 no params, 429 too many.  *
 *****************************************************************/

const REPORTS_DOOR = '/report-engine/api/reports/lookup';
const NOT_CONNECTED_ANSWERS = [401, 403, 404, 503]; // door missing, or this login cannot use it

/* ---- askReportsDoor(email, address) ----------------------------
   One call. Out: { state: 'ok' | 'not-connected' | 'trouble',
   reports: [] }.                                                */
async function askReportsDoor(email, address) {
  const queryText = buildQuery({ email, address });
  let doorResponse = null;

  try {
    doorResponse = await fetch(`${REPORTS_DOOR}?${queryText}`, { credentials: 'same-origin' });
  } catch (networkProblem) {
    return { state: 'trouble', reports: [] };
  }

  if (NOT_CONNECTED_ANSWERS.includes(doorResponse.status)) {
    return { state: 'not-connected', reports: [] };
  }

  if (!doorResponse.ok) {
    return { state: 'trouble', reports: [] };
  }

  let doorJson = null;

  try {
    doorJson = await doorResponse.json();
  } catch (notJson) {
    return { state: 'not-connected', reports: [] };
  }

  const wrappedData = doorJson && doorJson.data ? doorJson.data : doorJson;
  const reportList = wrappedData && Array.isArray(wrappedData.reports) ? wrappedData.reports : [];

  return { state: 'ok', reports: reportList };
}

/* ---- fetchPastReports({ email, addresses }) --------------------
   Every past report for a customer's email and/or any of their
   property addresses, each report once, newest first.
   One call per address (each also carries the email); one call
   with the email alone when there are no addresses.
   Out: { state, reports }. If any call says "not connected",
   the whole answer says so (honest UI).                         */
export async function fetchPastReports(lookFor) {
  const email = (lookFor.email || '').trim();
  const addresses = (lookFor.addresses || []).map((oneAddress) => String(oneAddress || '').trim()).filter(Boolean);

  if (!email && addresses.length === 0) {
    return { state: 'nothing-to-look-for', reports: [] };
  }

  const callPlans = addresses.length > 0
    ? addresses.map((oneAddress) => ({ email, address: oneAddress }))
    : [{ email, address: '' }];

  const callAnswers = await Promise.all(callPlans.map((onePlan) => askReportsDoor(onePlan.email, onePlan.address)));

  const anyNotConnected = callAnswers.some((oneAnswer) => oneAnswer.state === 'not-connected');

  if (anyNotConnected) {
    return { state: 'not-connected', reports: [] };
  }

  const allAnswersOk = callAnswers.every((oneAnswer) => oneAnswer.state === 'ok');

  if (!allAnswersOk) {
    return { state: 'trouble', reports: [] };
  }

  const reportsById = new Map();

  for (const oneAnswer of callAnswers) {
    for (const oneReport of oneAnswer.reports) {
      reportsById.set(oneReport.id, oneReport);
    }
  }

  const uniqueReports = Array.from(reportsById.values());

  uniqueReports.sort((firstReport, secondReport) => (
    String(secondReport.inspectionDate || secondReport.sentAt || '')
      .localeCompare(String(firstReport.inspectionDate || firstReport.sentAt || ''))
  ));

  return { state: 'ok', reports: uniqueReports };
}

/*****************************************************************
 *  SECTION 7 - THE HR LOOK'S COUNTS (2026-10-15)                  *
 *  What it does: the numbers the HR-style lists show above the    *
 *  table, read from doors that already exist:                     *
 *    fetchListTotals   "312 customers - 40 new this week"         *
 *    fetchBridgeWaiting  how many Zoho records wait at the Zoho   *
 *                        Bridge (owner / admin only)              *
 *  Reads only. Never changes anything.                            *
 *****************************************************************/

const ONE_ROW = 1;                 // a count needs the total, not the rows
const BRIDGE_SUMMARY_DOOR = '/bridge/zoho/summary'; // the Zoho Bridge's own panel summary

/* ---- fetchListTotals(fetchPageFor, weekViewKey) ------------------
   In: fetchCustomerPage or fetchPropertyPage; the "this week" view
   key ('new-this-week'), or null when the list has none.
   Out: { all, thisWeek } (thisWeek null when not asked), or null
   when the door cannot be reached. Unfiltered, like HR's count. */
export async function fetchListTotals(fetchPageFor, weekViewKey) {
  try {
    const allAnswer = await fetchPageFor({ view: 'all', page: 1, limit: ONE_ROW });
    const allTotal = Number(allAnswer.meta.total) || 0;

    if (!weekViewKey) {
      return { all: allTotal, thisWeek: null };
    }

    const weekAnswer = await fetchPageFor({ view: weekViewKey, page: 1, limit: ONE_ROW });
    const weekTotal = Number(weekAnswer.meta.total) || 0;

    return { all: allTotal, thisWeek: weekTotal };
  } catch (countProblem) {
    return null;
  }
}

/* ---- fetchBridgeWaiting() ----------------------------------------
   Out: { contacts, properties } still waiting for a yes at the Zoho
   Bridge, or null when this login may not see the Bridge (it is
   for the company's owner and admins) or the door is down.
   WHY (2026-10-15, James via Fable): the HR look's banner says
   "8,604 contacts are waiting at the Zoho Bridge" - honestly, from
   the Bridge's own count, and only to the people who can act.   */
export async function fetchBridgeWaiting() {
  try {
    const doorResponse = await authFetch(`${API}${BRIDGE_SUMMARY_DOOR}`, { method: 'GET' });

    if (!doorResponse.ok) {
      return null;
    }

    const doorJson = await doorResponse.json();
    const summary = doorJson && doorJson.data ? doorJson.data : doorJson;
    const waitingPile = summary && summary.pile && summary.pile.proposed ? summary.pile.proposed : null;

    if (!waitingPile) {
      return null;
    }

    return {
      contacts: Number(waitingPile.crm_contact) || 0,
      properties: Number(waitingPile.crm_property) || 0,
    };
  } catch (doorProblem) {
    return null;
  }
}

/*****************************************************************
 *  SECTION 8 - A CUSTOMER'S TASKS (2026-10-15)                    *
 *  For Zoho's "Open Activities" / "Closed Activities" columns on  *
 *  the record page. Reads only.                                   *
 *****************************************************************/

/* ---- fetchCustomerTasks(customerId) -- every task for one customer ---- */
export function fetchCustomerTasks(customerId) {
  return apiGet(`/customer/tasks?customerId=${encodeURIComponent(customerId)}`);
}
